// useKeyboard.ts — keyboard shortcut foundation (W-CC-04).
//
// Two primitives:
//   * `useKeyboardShortcut(combo, handler, opts)` — register a single shortcut
//     for the lifetime of the calling component. Supports modifier combos
//     ("Mod+K", "Shift+/") and chord sequences ("g r").
//   * `KeyboardContext` — exposes a registry so components can introspect
//     the active shortcuts (the help overlay reads this).
//
// `Mod` resolves to ⌘ on macOS and Ctrl elsewhere.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  createElement,
} from 'react';

export interface KeyboardShortcutDescriptor {
  combo: string;
  label: string;
  group?: string;
}

interface KeyboardContextValue {
  shortcuts: KeyboardShortcutDescriptor[];
  registerShortcut: (descriptor: KeyboardShortcutDescriptor) => () => void;
}

const KeyboardContext = createContext<KeyboardContextValue | null>(null);

/**
 * One entry, for the overlay's purposes: the same key can be answered by the
 * shell and by a page that is spending it on something of its own (`/` is the
 * command palette, and the event list's More filters), and the overlay has to
 * be able to say both. What it must not print twice is one shortcut whose
 * component mounted twice.
 */
function sameShortcut(
  a: KeyboardShortcutDescriptor,
  b: KeyboardShortcutDescriptor
): boolean {
  return a.combo === b.combo && a.label === b.label && a.group === b.group;
}

export function KeyboardProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [shortcuts, setShortcuts] = useState<KeyboardShortcutDescriptor[]>(
    []
  );

  const registerShortcut = useCallback(
    (descriptor: KeyboardShortcutDescriptor) => {
      setShortcuts((prev) => {
        if (prev.some((s) => sameShortcut(s, descriptor))) return prev;
        return [...prev, descriptor];
      });
      return () => {
        // Preserve the previous reference when nothing was removed: an
        // unconditional `filter` returns a NEW array every call, which reads
        // as a state change, recomputes the context value, and re-runs every
        // consumer's register effect — a self-sustaining re-render loop (React
        // #185 on back-navigation, and enough churn to keep interrupting
        // router transitions so Link navigations never committed).
        setShortcuts((prev) => {
          const next = prev.filter((s) => !sameShortcut(s, descriptor));
          return next.length === prev.length ? prev : next;
        });
      };
    },
    []
  );

  const value = useMemo<KeyboardContextValue>(
    () => ({ shortcuts, registerShortcut }),
    [shortcuts, registerShortcut]
  );

  return createElement(KeyboardContext.Provider, { value }, children);
}

export function useKeyboardRegistry(): KeyboardContextValue {
  const ctx = useContext(KeyboardContext);
  if (!ctx) {
    throw new Error(
      'useKeyboardRegistry must be used inside <KeyboardProvider>.'
    );
  }
  return ctx;
}

/**
 * The registry when a `KeyboardProvider` is mounted, else null — for a surface
 * that lists its keys in the overlay but has to work without one (a unit test
 * or a story rendering one list on its own).
 */
export function useOptionalKeyboardRegistry(): KeyboardContextValue | null {
  return useContext(KeyboardContext);
}

interface ShortcutOptions {
  /** Human label for the help overlay. */
  label?: string;
  /** Group key for the help overlay (e.g. "Navigation", "Editing"). */
  group?: string;
  /** When true, shortcuts trigger even inside input/textarea/contentEditable. */
  allowInInputs?: boolean;
  /** Pause the shortcut without unmounting the component. */
  enabled?: boolean;
  /** Override the registry label/visibility — pass false to keep it hidden. */
  registerInHelp?: boolean;
}

const isMacLike = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
};

interface ParsedAtom {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

function parseAtom(atom: string): ParsedAtom {
  const parts = atom.split('+').map((p) => p.trim());
  const out: ParsedAtom = {
    key: '',
    ctrl: false,
    alt: false,
    shift: false,
    meta: false,
  };
  const mac = isMacLike();
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (lower === 'mod') {
      if (mac) out.meta = true;
      else out.ctrl = true;
    } else if (lower === 'cmd' || lower === 'meta') out.meta = true;
    else if (lower === 'ctrl' || lower === 'control') out.ctrl = true;
    else if (lower === 'alt' || lower === 'option') out.alt = true;
    else if (lower === 'shift') out.shift = true;
    else out.key = normalizeKey(lower);
  }
  return out;
}

function normalizeKey(key: string): string {
  if (key === 'space') return ' ';
  if (key === 'esc') return 'escape';
  if (key === 'return') return 'enter';
  return key;
}

function matchesAtom(atom: ParsedAtom, event: KeyboardEvent): boolean {
  if (atom.ctrl !== event.ctrlKey) return false;
  if (atom.alt !== event.altKey) return false;
  if (atom.meta !== event.metaKey) return false;
  // Shift is special: when atom.shift is false but the user must press Shift
  // for the printable character (e.g. "?"), we let the key compare succeed.
  if (atom.shift && !event.shiftKey) return false;
  const eventKey = (event.key || '').toLowerCase();
  return eventKey === atom.key;
}

/** The character Shift makes of a printable key, when it has a name of its own. */
const SHIFTED: Record<string, string> = { '/': '?', ',': '<', '.': '>', ';': ':', "'": '"' };

/** A key as a reader knows it: `k` -> `K`, `escape` -> `Esc`, `arrowup` -> `Up`. */
function keyLabel(key: string): string {
  if (key === ' ') return 'Space';
  if (key === 'escape') return 'Esc';
  if (key.startsWith('arrow')) {
    const direction = key.slice('arrow'.length);
    return direction.charAt(0).toUpperCase() + direction.slice(1);
  }
  if (key.length === 1) return key.toUpperCase();
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * A combo the way it is printed on a key: `mod+k` -> `⌘K` on macOS and
 * `Ctrl+K` elsewhere, `shift+/` -> `?`, and a chord `g n` -> `G N`. The
 * overlay shows this, never the combo the code binds.
 */
export function formatCombo(combo: string): string {
  const mac = isMacLike();
  return combo
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => {
      const atom = parseAtom(part);
      // Shift is part of the character when the character has its own name:
      // `?` is what the reader presses, not `Shift+/`.
      const shifted = atom.shift ? SHIFTED[atom.key] : undefined;
      const key = shifted ?? keyLabel(atom.key);
      const modifiers: string[] = [];
      if (atom.ctrl) modifiers.push(mac ? '⌃' : 'Ctrl');
      if (atom.alt) modifiers.push(mac ? '⌥' : 'Alt');
      if (atom.shift && !shifted) modifiers.push(mac ? '⇧' : 'Shift');
      if (atom.meta) modifiers.push(mac ? '⌘' : 'Meta');
      if (modifiers.length === 0) return key;
      return mac ? `${modifiers.join('')}${key}` : `${modifiers.join('+')}+${key}`;
    })
    .join(' ');
}

/**
 * True when focus is in a text-entry surface (input/textarea/select/
 * contentEditable) — used to suppress single-key shortcuts while typing.
 * Exported so other keyboard surfaces (e.g. the boot carousel) reuse one guard.
 */
export function isTypingInTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * A key a page read for itself, in the capture phase. The registry's own
 * bindings for the same key stand aside: `/` is the command palette
 * everywhere, and the one page that spends it on its own field says so here
 * rather than the shell having to know which pages those are.
 */
const claimedKeys = new WeakSet<KeyboardEvent>();

/** Claim this key event: registry bindings for it will not fire. */
export function claimKey(event: KeyboardEvent): void {
  claimedKeys.add(event);
}

/** True when a page already answered this key event itself. */
export function isKeyClaimed(event: KeyboardEvent): boolean {
  return claimedKeys.has(event);
}

/**
 * Print keys a surface reads itself in the help overlay, binding nothing. A
 * list that handles its own `keydown` — to leave Enter to the button under the
 * reader, or the arrows to the page's scrolling — still belongs in the list of
 * what the page answers.
 */
export function useShortcutHelp(
  descriptors: readonly KeyboardShortcutDescriptor[]
): void {
  const ctx = useContext(KeyboardContext);
  const registerShortcut = ctx?.registerShortcut ?? null;
  const latest = useRef(descriptors);
  latest.current = descriptors;
  // The descriptors are written inline at the call site, so a new array every
  // render: what they say is the dependency, not which array says it.
  const key = JSON.stringify(descriptors);
  useEffect(() => {
    if (!registerShortcut) return () => {};
    const unregister = latest.current.map((descriptor) =>
      registerShortcut(descriptor)
    );
    return () => {
      for (const off of unregister) off();
    };
  }, [key, registerShortcut]);
}

const CHORD_TIMEOUT_MS = 1200;

export function useKeyboardShortcut(
  combo: string,
  handler: (event: KeyboardEvent) => void,
  options: ShortcutOptions = {}
): void {
  const ctx = useContext(KeyboardContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const atoms = useMemo(() => {
    return combo
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(parseAtom);
  }, [combo]);

  // Depend on the STABLE register function, never on the context value: the
  // value recomputes whenever the shortcuts list changes, so using it as a
  // dep re-runs every consumer's register effect on every registry change —
  // each re-run unregisters/re-registers (two more updates) and the loop
  // never settles.
  const registerShortcut = ctx?.registerShortcut ?? null;
  useEffect(() => {
    if (options.registerInHelp === false || !registerShortcut) {
      return () => {};
    }
    return registerShortcut({
      combo,
      label: options.label ?? combo,
      group: options.group,
    });
  }, [
    combo,
    registerShortcut,
    options.label,
    options.group,
    options.registerInHelp,
  ]);

  useEffect(() => {
    if (options.enabled === false || atoms.length === 0) return () => {};
    let chordIndex = 0;
    let chordTimer: ReturnType<typeof setTimeout> | null = null;

    const onKey = (event: KeyboardEvent): void => {
      if (!options.allowInInputs && isTypingInTextField(event.target)) {
        return;
      }
      // A page that read this key in the capture phase has answered it.
      if (isKeyClaimed(event)) {
        chordIndex = 0;
        if (chordTimer) clearTimeout(chordTimer);
        chordTimer = null;
        return;
      }
      const expected = atoms[chordIndex];
      if (!expected || !matchesAtom(expected, event)) {
        chordIndex = 0;
        if (chordTimer) clearTimeout(chordTimer);
        chordTimer = null;
        return;
      }
      event.preventDefault();
      chordIndex += 1;
      if (chordIndex >= atoms.length) {
        chordIndex = 0;
        if (chordTimer) clearTimeout(chordTimer);
        chordTimer = null;
        handlerRef.current(event);
        return;
      }
      if (chordTimer) clearTimeout(chordTimer);
      chordTimer = setTimeout(() => {
        chordIndex = 0;
      }, CHORD_TIMEOUT_MS);
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (chordTimer) clearTimeout(chordTimer);
    };
  }, [atoms, options.enabled, options.allowInInputs]);
}
